import Editor from '@ckeditor/ckeditor5-core/src/editor';
import DecoupledEditor from '@ckeditor/ckeditor5-editor-decoupled/src/decouplededitor';
import {
  disablePlaceholder,
  enablePlaceholder,
} from '@ckeditor/ckeditor5-engine/src/view/placeholder';
import EditorWatchdog from '@ckeditor/ckeditor5-watchdog/src/editorwatchdog';
import React, { Component, createRef, RefObject } from 'react';
import { EDITOR_LOCK_KEY } from 'constant';
import { logger } from 'services';
import { queueMacrotask } from 'utils/macrotask';

type EditorData = string;

interface Props {
  data: EditorData;
  config: Record<string, any>;
  readonly: boolean;
  placeholder?: string;
  onInit?: (editor: Editor) => void;
  onChange?: (data: EditorData) => void;
  onBlur?: (data: EditorData) => void;
  onFocus?: (data: EditorData) => void;
  onError?: (error: Error) => void;
}

export const CKE5_NODE_CLASS = 'ck-body-wrapper';

export class CKEditor5 extends Component<Props, Record<string, unknown>> {
  container: RefObject<HTMLDivElement>;

  editor: Editor | null;

  config: Record<string, any>;

  watchdog: any;

  constructor(props: Props) {
    super(props);
    this.editor = null;
    this.container = createRef();
    this.config = props.config;
    this.watchdog = null;
  }

  componentDidMount() {
    const { data, readonly, onChange, onFocus, onBlur, onInit, onError } =
      this.props;

    const { container } = this;

    this.watchdog = new EditorWatchdog(DecoupledEditor);

    this.watchdog.setCreator((elementOrData, editorConfig) => {
      return DecoupledEditor.create(elementOrData, editorConfig)
        .then((editor: Editor) => {
          this.editor = editor;
          this.toggleReadOnlyMode(readonly);
          const modelDocument = editor.model.document;
          const viewDocument = editor.editing.view.document;

          modelDocument.on('change:data', () => onChange?.(editor.getData()));
          viewDocument.on('focus', () => onFocus?.(editor.getData()));
          viewDocument.on('blur', () => onBlur?.(editor.getData()));
          const el = this.editor.ui.getEditableElement();

          queueMacrotask(() => {
            if (container.current !== null) {
              container.current.replaceWith(el);
            }
          });
          onInit?.(this.editor);

          return editor;
        })
        .catch((error: Error) => {
          onError?.(error);
          logger.error('DecoupledEditor create failed', { error });
        });
    });

    if (!data && typeof data !== 'string') {
      logger.error(`data is falsy and rte editor should be broken, ${data}`, {
        error: new Error('data is falsy'),
      });
    }
    // Create a new editor instance.
    this.watchdog.create(data, this.config);

    this.watchdog.on('error', (evt, { error, causesRestart }) => {
      logger.warn('Editor crashed.', { evt, error, causesRestart });
    });
    this.watchdog.on('restart', () => {
      logger.info('Editor was restarted.');
    });
    this.watchdog.on('crashedPermanently', (args) => {
      logger.warn(`Editor crashed permanently ${JSON.stringify(args)}`);
    });
  }

  /**
   * We should never re-render this component from react. We make the necessary
   * updates via the editor object, and return false.
   * @param props The props passed to the component.
   */
  shouldComponentUpdate({ data, readonly, placeholder }: Props) {
    if (this.editor == null) {
      return false;
    }
    if (this.shouldUpdateData(data)) {
      this.editor.setData(data);
    }
    this.toggleReadOnlyMode(readonly);
    if (this.config.placeholder !== placeholder) {
      this.updatePlaceholder(placeholder);
    }

    return false;
  }

  componentWillUnmount() {
    this.editor?.destroy().then(() => {
      this.editor = null;
    });
    this.watchdog?.destroy();
  }

  toggleReadOnlyMode(isReadOnly) {
    if (!this.editor) {
      return;
    }
    if (isReadOnly) {
      this.editor.enableReadOnlyMode(EDITOR_LOCK_KEY);
    } else {
      this.editor.disableReadOnlyMode(EDITOR_LOCK_KEY);
    }
  }

  private updatePlaceholder(text?: string) {
    const editor = this.editor!;
    const { view } = editor.editing;
    const element = view.document.getRoot();

    if (text != null) {
      enablePlaceholder({
        view,
        element,
        text,
        isDirectHost: false,
      });
    } else {
      disablePlaceholder(view, element);
    }
    this.config.placeholder = text;
  }

  private shouldUpdateData(next: EditorData) {
    const { data } = this.props;

    if (data === next) {
      return false;
    }
    if (this.editor!.getData() === next) {
      return false;
    }

    return true;
  }

  render() {
    return <div ref={this.container} />;
  }
}
